import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../../components/Inputs/Select";

const getColumnId = (column) => {
	if (column?.dataIndex) return column.dataIndex;
	const name = (column?.name || "").replaceAll(".", "_");
	return column?.aggregation ? `${name}_${column.aggregation}` : name;
};

const getColumnHeader = (column, t) =>
	column?.alias ||
	column?.display_name ||
	column?.header ||
	t((column?.name || column?.id || "").replaceAll(/[_\\.]/g, " "));

const getIncludedColumns = (columns, excludedColumns = []) =>
	columns
		.map(({ id }) => id)
		.filter((id) => !excludedColumns.includes(id));

function ExportColumnsSelect({ report, onExcludedColumnsChange }) {
	const { t } = useTranslation();

	const columns = useMemo(() => {
		if (report?.exportColumns?.length) {
			return report.exportColumns;
		}

		return (report?.reportData?.columns || [])
			.filter((column) => !column?.hasExtraColumns)
			.map((column) => ({
				id: getColumnId(column),
				header: getColumnHeader(column, t),
			}));
	}, [report?.exportColumns, report?.reportData?.columns, t]);

	const [includedColumns, setIncludedColumns] = useState(() =>
		getIncludedColumns(columns, report?.excludedColumns)
	);

	// Seed from the view's visible columns only when the export popup opens
	useEffect(() => {
		setIncludedColumns(getIncludedColumns(columns, report?.excludedColumns));
	}, [report, columns]);

	useEffect(() => {
		const excludedColumns = columns
			.map(({ id }) => id)
			.filter((id) => !includedColumns.includes(id));

		onExcludedColumnsChange?.(excludedColumns);
	}, [columns, includedColumns, onExcludedColumnsChange]);

	if (!columns.length) {
		return null;
	}

	return (
		<Select
			mode="multiple"
			allowClear
			placeholder={t("columns")}
			value={includedColumns}
			onChange={(value) => setIncludedColumns(value || [])}
		>
			{columns.map(({ id, header }) => (
				<Select.Option key={id} value={id}>
					{header}
				</Select.Option>
			))}
		</Select>
	);
}

export default ExportColumnsSelect;
